import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CommonDataTable from "../../../components/CommonDataTable";
import ConfirmDialog from "../../../components/ConfirmDialog";
import { toast } from "../../../components/AppToast";
import ShipAddressModal from "./ShipAddressModal";
import {
  buildShipPayload,
  emptyOperationalHours,
  emptyShipForm,
  flattenShipRecord,
  hydrateOperationalHours,
  shipFieldDefs,
  validateShipForm,
} from "./storerForm";

/**
 * Ship To (customer) / Ship From (supplier) list on the edit and view screens.
 * Loads and saves through the parent entity's ship API.
 */
export default function PartnerShipTab({
  entityLabel = "Customer",
  partnerRow,
  readOnly = false,
  ship,
}) {
  const title = ship?.title || (entityLabel === "Supplier" ? "Ship From" : "Ship To");
  const prefix = ship?.prefix || (entityLabel === "Supplier" ? "shipfrom" : "shipto");
  const showOperationalHours = prefix === "shipto";
  const fields = useMemo(() => shipFieldDefs(prefix, title), [prefix, title]);
  const keyField = `${prefix}_id`;
  const parentId = partnerRow?._id;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [operationalHours, setOperationalHours] = useState(() => emptyOperationalHours());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const loadList = useCallback(async (pageNo = 0, size = pageSize) => {
    if (!parentId || typeof ship?.list !== "function") {
      setRows([]);
      setTotalCount(0);
      return;
    }
    setLoading(true);
    try {
      const res = await ship.list(pageNo, size, { [ship.parentIdField]: parentId });
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(list.map((rec) => flattenShipRecord(rec, prefix)));
      setTotalCount(Number(res?.total) || 0);
    } catch (err) {
      toast.error(err?.message || `Failed to load ${title}`);
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [parentId, pageSize, prefix, ship, title]);

  useEffect(() => {
    loadList(page, pageSize);
  }, [loadList, page, pageSize]);

  const openAdd = () => {
    if (!parentId) {
      toast.error(`Save the ${entityLabel.toLowerCase()} before adding a ${title.toLowerCase()}`);
      return;
    }
    setErrors({});
    setForm(emptyShipForm(prefix));
    setOperationalHours(emptyOperationalHours());
    setModal({ mode: "add", id: null });
  };

  const openRow = (row) => {
    if (!row) return;
    setErrors({});
    const { operationalHours: savedHours, ...rest } = row;
    setForm({ ...emptyShipForm(prefix), ...rest, status: row.status || "Active" });
    setOperationalHours(hydrateOperationalHours(savedHours));
    setModal({ mode: "edit", id: row._id || row.id });
  };

  const closeModal = () => {
    if (saving) return;
    setModal(null);
    setErrors({});
  };

  const handleSave = async (data) => {
    const nextErrors = validateShipForm(data, prefix, title, {
      operationalHours,
      requireOperationalHours: showOperationalHours,
    });
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      toast.warning(nextErrors.operationalHours || "Please fill the required fields");
      return;
    }
    if (!parentId) {
      toast.error(`Missing ${entityLabel.toLowerCase()} id`);
      return;
    }

    const payload = buildShipPayload(data, {
      prefix,
      parentIdField: ship.parentIdField,
      parentId,
      operationalHours,
      includeOperationalHours: showOperationalHours,
    });

    setSaving(true);
    try {
      if (modal?.mode === "edit" && modal.id) {
        await ship.update(payload, modal.id);
        toast.success(`${title} updated`);
      } else {
        await ship.create(payload);
        toast.success(`${title} created`);
      }
      setModal(null);
      setErrors({});
      if (modal?.mode !== "edit" && page !== 0) setPage(0);
      else loadList(modal?.mode === "edit" ? page : 0, pageSize);
    } catch (err) {
      toast.error(err?.message || `Failed to save ${title.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenMenu = (e, row) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuRow(row);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  };

  const handleMenuOpenRow = () => {
    openRow(menuRow);
    handleCloseMenu();
  };

  const handleMenuDelete = () => {
    setConfirmDelete(menuRow);
    handleCloseMenu();
  };

  const confirmDeleteRow = async () => {
    const id = confirmDelete?._id || confirmDelete?.id;
    if (!id) return;
    try {
      await ship.remove(id);
      toast.success(`${title} deleted`);
      loadList(page, pageSize);
    } catch (err) {
      toast.error(err?.message || `Failed to delete ${title.toLowerCase()}`);
    }
  };

  const handleExport = (selectedRows) => {
    const source = Array.isArray(selectedRows) && selectedRows.length ? selectedRows : rows;
    if (!source.length) {
      toast.info(`No ${title.toLowerCase()} rows to export`);
      return;
    }
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = fields.map((f) => escape(f.label)).join(",");
    const body = source.map((r) => fields.map((f) => escape(r[f.name])).join(",")).join("\n");
    const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${entityLabel.toLowerCase()}-${prefix}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = useMemo(() => {
    const actionsColumn = {
      field: "__actions",
      headerName: "",
      width: 72,
      minWidth: 72,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          aria-label={`${title} actions`}
          onClick={(e) => handleOpenMenu(e, params.row)}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    };

    return [
      actionsColumn,
      ...fields.map((f) => ({
        field: f.name,
        headerName: f.label,
        width: f.name === keyField ? 140 : f.name.endsWith("_name") ? 220 : 160,
      })),
    ];
  }, [fields, keyField, title]);

  const visibleFields = useMemo(() => ["__actions", ...fields.map((f) => f.name)], [fields]);
  const parentName = partnerRow?.partner_name || partnerRow?.partner_id || "";

  return (
    <div className="pt-2">
      <p className="text-muted small mb-2">
        {title} for <strong>{parentName || entityLabel}</strong>
      </p>
      {loading && <div className="text-center py-2">Loading {title}...</div>}
      <CommonDataTable
        id={`${entityLabel.toLowerCase()}-${prefix}-table`}
        allPossibleColumns={columns}
        initialVisible={visibleFields}
        initialRows={rows}
        height={420}
        rowsPerPageOptions={[10, 25, 50]}
        checkboxSelection
        showCreateButton={!readOnly}
        createButtonText={`Add ${title}`}
        createButtonIcon="bi-plus-circle"
        onCreateClick={readOnly ? undefined : openAdd}
        exportImportPath={`${entityLabel.toLowerCase()}-${prefix}`}
        sampleFileName={`${entityLabel.toLowerCase()}-${prefix}`}
        enableImportButton={false}
        onExport={handleExport}
        linkColumns={{ [keyField]: { underline: "always" } }}
        onLinkOpen={({ row }) => openRow(row)}
        page={page}
        pageSize={pageSize}
        rowCount={totalCount}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPage(0);
        }}
        onManualRefresh={() => loadList(page, pageSize)}
      />

      <Menu
        open={Boolean(menuAnchorEl)}
        anchorEl={menuAnchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem onClick={handleMenuOpenRow}>
          <i className={`bi ${readOnly ? "bi-eye" : "bi-pencil"} me-2`}></i>
          {readOnly ? "View" : "Edit"}
        </MenuItem>
        {!readOnly && (
          <MenuItem onClick={handleMenuDelete}>
            <i className="bi bi-trash me-2"></i> Delete
          </MenuItem>
        )}
      </Menu>

      <ShipAddressModal
        show={!!modal}
        title={modal?.mode === "add" ? `Add ${title}` : readOnly ? `View ${title}` : `Edit ${title}`}
        fields={fields}
        form={form}
        errors={errors}
        onChange={(next) => {
          setForm(next);
          setErrors({});
        }}
        onClose={closeModal}
        onSave={handleSave}
        readOnly={readOnly}
        saving={saving}
        showOperationalHours={showOperationalHours}
        operationalHours={operationalHours}
        onOperationalHoursChange={(next) => {
          setOperationalHours(next);
          setErrors((prev) => ({ ...prev, operationalHours: undefined }));
        }}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        title={`Delete ${title}?`}
        message={`Delete ${confirmDelete?.[keyField] || confirmDelete?.[`${prefix}_name`] || "this record"}?`}
        yesText="Delete"
        noText="Cancel"
        onClose={() => setConfirmDelete(null)}
        onConfirm={confirmDeleteRow}
      />
    </div>
  );
}
